import type { TourRequest, TourResult } from "./types";

/**
 * Solve a single-vehicle routing (TSP) instance **exactly** with the
 * Held–Karp dynamic program, off the main thread in a dedicated module
 * Web Worker. Pure TypeScript — no WASM download, no network — so the first
 * solve is as warm as the last.
 *
 * The returned status is always "OPTIMAL": Held–Karp enumerates every
 * subset-state, so a result is a proof, not a best-effort (there is no time
 * limit to hit — `timeLimitSec` on the request is ignored). Instances above
 * 20 customers are rejected by the algorithm's memory guard and reject the
 * promise instead.
 */

interface ExactOkMsg {
  id: number;
  ok: true;
  result: { length: number; order: number[]; states: number; tBuildMs: number; tSolveMs: number };
}
interface ExactErrMsg {
  id: number;
  ok: false;
  error: string;
}

let worker: Worker | null = null;
function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL("./exactTour.worker.ts", import.meta.url), { type: "module" });
  }
  return worker;
}

let seq = 0;

export function solveTourExact(req: TourRequest): Promise<TourResult> {
  const w = getWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      w.removeEventListener("message", onMsg);
      w.removeEventListener("error", onErr);
    };
    const onMsg = (e: MessageEvent<ExactOkMsg | ExactErrMsg>) => {
      const d = e.data;
      if (d.id !== id) return;
      cleanup();
      if (d.ok) {
        resolve({
          status: "OPTIMAL",
          length: d.result.length,
          order: d.result.order,
          tBuildMs: d.result.tBuildMs,
          tSolveMs: d.result.tSolveMs,
        });
      } else {
        reject(new Error(d.error || "Held–Karp solver error"));
      }
    };
    const onErr = (e: ErrorEvent) => {
      cleanup();
      reject(new Error("Exact-tour worker failed to load: " + (e.message || "unknown error")));
    };
    w.addEventListener("message", onMsg);
    w.addEventListener("error", onErr);
    w.postMessage({ id, points: req.points, dist: req.dist });
  });
}
